import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { FloatLabelType } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import * as _ from 'lodash-es';

@Component({
  selector: 'vi-input',
  templateUrl: 'input.component.html',
  styleUrls: ['input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViInputComponent implements OnChanges, AfterViewInit {
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() inputFocus: EventEmitter<void> = new EventEmitter<void>();
  @Output() inputBlur: EventEmitter<void> = new EventEmitter<void>();
  @Output() autocompleteClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() autocompleteOpen: EventEmitter<void> = new EventEmitter<void>();
  @Output() autocompleteSelect: EventEmitter<{
    value: any;
    viewValue: string;
    inputViewValue?: string;
  }> = new EventEmitter<{ value: any; viewValue: string; inputViewValue?: string }>();

  @Input() placeholder: string;
  @Input() value: string | { value: any; viewValue: string; inputViewValue?: string } = null;
  @Input() isClearable: Boolean = false;
  @Input() width = '100%';
  @Input() type = 'text';
  @Input() errorMessage: string;
  @Input() disabled = false;
  @Input() showError = false;
  @Input() icon: String;
  @Input() iconColor: String;
  @Input() bgColor: String;
  @Input() size: String;
  @Input() isFloatLabel = true;
  @Input() autocomplete = false;
  @Input() maxInputLength = 128;
  @Input() maxAutocompleteLength = null;
  @Input() autocompleteOptions: {
    value: any;
    viewValue: string;
    inputViewValue?: string;
    column?: string[];
  }[];
  @Input() isFocused = true;
  @Input() required = false;
  @Input() forceAutocompleteOpen: boolean;

  @ViewChild(MatInput) matInput: MatInput;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  constructor(protected cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    if (this.forceAutocompleteOpen && this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.cdr.markForCheck();
    const errorMessageChanged =
      changes?.errorMessage?.previousValue !== changes?.errorMessage?.currentValue &&
      !changes?.errorMessage?.firstChange;

    if (errorMessageChanged) {
      this.cdr.markForCheck();
    }
  }

  onChange(value: any): void {
    const textToDisplay = value?.inputViewValue ? value?.inputViewValue : value?.viewValue;
    this.valueChange.emit(_.isString(value) || !value ? value : textToDisplay);
    this.cdr.markForCheck();
  }

  onFocus(): void {
    this.isFocused = true;
    this.inputFocus.emit();
  }

  onBlur(): void {
    this.isFocused = false;
    this.inputBlur.emit();
  }

  onAutocompleteClose(): void {
    this.autocompleteClose.emit();
  }

  onAutocompleteOpen(): void {
    this.autocompleteOpen.emit();
  }

  onAutocompleteSelect(event: MatAutocompleteSelectedEvent): void {
    this.autocompleteSelect.emit(event.option.value);
  }

  clear(): void {
    this.value = '';
    this.onChange('');
  }

  getFloatLabel(): FloatLabelType {
    return this.isFloatLabel ? 'always' : 'auto';
  }

  displayFn(option: { value: any; viewValue: string; inputViewValue?: string }): string | null {
    const textToDisplay = option?.inputViewValue ? option?.inputViewValue : option?.viewValue;

    return option ? textToDisplay : null;
  }
}
